import NotFound from "@components/modules/error-pages/NotFound";
import LoadingPage from "@components/modules/loading/LoadingPage";
import SuperAdminLayout from "@components/pages/superAdmin/SuperAdminLayout";
import superAdminNavigation, { superAdminRoute } from "@setup/route_config/superAdminRoutes";
import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const LoginPage = lazy(() => import("@components/pages/superAdmin/login/Login"));

// Where to send a logged-in non-superAdmin who lands on /root.
// /root is reserved for superAdmin only — every other role gets bounced
// out of the /root basename entirely (window.location, not navigate(),
// because navigate() inside /root would re-mount RootRouting on a new path).
const NON_SUPERADMIN_LANDING: Record<string, string> = {
  admin: "/admin/dashboard",
  teacher: "/teacher",
  "parent-teacher": "/parent-teacher",
  parent: "/parent",
  student: "/student",
  registrar: "/registrar/students",
};

const RootRouting = () => {
  const { baseRole, loading, session } = useAuth();

  const isSuperAdmin = baseRole === "super-admin";
  const isAuthenticatedNonSuperAdmin =
    !loading && !!session && !!baseRole && baseRole !== "super-admin";

  // SECURITY: bounce non-superAdmin authenticated users out of /root.
  // Without this guard, RootRouting checked only "is there any profile?"
  // and sent every logged-in user into the SuperAdmin layout. RLS limited
  // the data damage but the SuperAdmin nav, page shells, and any
  // client-side org list could leak through.
  useEffect(() => {
    if (!isAuthenticatedNonSuperAdmin) return;
    const target = NON_SUPERADMIN_LANDING[baseRole as string] ?? "/";
    window.location.replace(target);
  }, [isAuthenticatedNonSuperAdmin, baseRole]);

  // While bouncing, render a loader instead of the SuperAdmin shell to
  // avoid a flash of the layout for a non-superAdmin during the redirect.
  if (isAuthenticatedNonSuperAdmin) {
    return <LoadingPage />;
  }

  // Hold on the loading spinner while Supabase resolves the session and
  // profile. Without this guard, baseRole is null during the async window
  // between INITIAL_SESSION (which unblocks loading) and the subsequent
  // profile fetch completing. null !== "super-admin", so isSuperAdmin is
  // false, and an already-authenticated super-admin sees the login page
  // momentarily. If app_metadata.base_role is not set for super-admin
  // accounts in Supabase, the window lasts until the DB round-trip
  // finishes — long enough to render the login form and potentially
  // redirect to /dashboard before the full route tree is mounted.
  if (loading) {
    return <LoadingPage />;
  }

  // Anonymous users (no session after loading completes) see the SuperAdmin
  // login page on ANY URL inside /root — not just the exact /root index.
  // Without this, visiting /root/dashboard (or any deep link) while logged
  // out fell through to the React NotFound page.
  if (!isSuperAdmin) {
    return (
      <Suspense fallback={<LoadingPage />}>
        <Routes>
          <Route path="*" element={<LoginPage />} />
        </Routes>
      </Suspense>
    );
  }

  // SuperAdmin: full route tree.
  return (
    <Suspense fallback={<LoadingPage />}>
      <Routes>
        <Route index element={<Navigate to="/dashboard" />} />

        <Route path="/" element={<SuperAdminLayout />}>
          {superAdminRoute.map((route, index) => (
            <Route key={index} path={route.path} element={route.component}>
              {(route?.children ?? []).map((sub_route, sub_index) => (
                <Route
                  key={sub_index}
                  path={sub_route.path}
                  element={sub_route.component}
                  index={sub_route.index}
                />
              ))}
            </Route>
          ))}

          {superAdminNavigation.map((group) =>
            group.routes.map((route, index) => (
              <Route key={index} path={route.path} element={route.component}>
                {(route?.children ?? []).map((sub_route, sub_index) => (
                  <Route
                    key={sub_index}
                    path={sub_route.path}
                    element={sub_route.component}
                    index={sub_route.index}
                  />
                ))}
              </Route>
            )),
          )}
        </Route>

        <Route path="/not-found" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default RootRouting;
